import { INetworkResult } from '@/coreDomainsModule/networkResult';
import {
  DashboardAlertApi,
  ApiDashboardAlertSetting,
  ApiSimulationSetting,
  SimulationApi,
  SuccessResponse,
  ErrorResponse,
} from '../axios/generated/settingManage';
import { IError } from '../interfaces/IError';
import { ISettingRepository, SimulationSettingSaveResult } from '../interfaces/ISettingRepository';
import { ApiBusinessFreeColumnSetting } from '../interfaces/ISettingRepository';
import { customAxiosInstance } from '../axios/axiosInstance';
import { axiosErrorHandler } from '../axios/axiosError';
import { AxiosResponse } from 'axios';
import { pollAsyncProcess } from '../composables/useAsyncPolling';

/**
 * 設定を永続化するリポジトリ
 */
export class SettingRepository implements ISettingRepository {
  /**
   * ダッシュボードアラート設定を取得する
   */
  async getDashboardAlertSetting(): Promise<INetworkResult<ApiDashboardAlertSetting, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const dashboardAlertApi = new DashboardAlertApi(undefined, undefined, axiosInstance);
    // APIを叩く
    try {
      const apiResponse = await dashboardAlertApi.get();
      switch (apiResponse.status) {
        case 200:
          const result: ApiDashboardAlertSetting = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (e) {
      return { status: { type: 'error', error: axiosErrorHandler(e) } };
    }
  }

  /**
   * ダッシュボードアラート設定を保存する
   * @param request - リクエスト
   */
  async putDashboardAlertSetting(request: ApiDashboardAlertSetting): Promise<INetworkResult<void, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const dashboardAlertApi = new DashboardAlertApi(undefined, undefined, axiosInstance);
    // APIを叩く
    try {
      const apiResponse = await dashboardAlertApi.put(request);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (e) {
      return { status: { type: 'error', error: axiosErrorHandler(e) } };
    }
  }

  /**
   * 自由欄設定を取得する
   */
  async getBusinessFreeColumnSetting(): Promise<INetworkResult<ApiBusinessFreeColumnSetting, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    try {
      if (!axiosInstance) throw new Error('axiosInstance is undefined');
      const apiResponse = await axiosInstance.get('/setting/business-free-columns');
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: apiResponse.data.result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (e) {
      return { status: { type: 'error', error: axiosErrorHandler(e) } };
    }
  }

  /**
   * 発注推奨数量端数処理設定を取得する
   */
  async getSimulationSetting(): Promise<INetworkResult<ApiSimulationSetting, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const simulationApi = new SimulationApi(undefined, undefined, axiosInstance);
    try {
      const apiResponse = await simulationApi.getSimulationSetting();
      switch (apiResponse.status) {
        case 200:
          const result: ApiSimulationSetting = apiResponse.data.result;
          return { status: { type: 'success', data: result } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (e) {
      return { status: { type: 'error', error: axiosErrorHandler(e) } };
    }
  }

  /**
   * 発注推奨数量端数処理設定を保存する
   * @param request - リクエスト
   */
  async putSimulationSetting(request: ApiSimulationSetting): Promise<INetworkResult<SimulationSettingSaveResult, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    const simulationApi = new SimulationApi(undefined, undefined, axiosInstance);

    let apiResponse: AxiosResponse<SuccessResponse, ErrorResponse>;
    try {
      apiResponse = await simulationApi.putSimulationSetting(request);

      if (apiResponse.status !== 200) {
        throw new Error('想定外のレスポンス');
      }

      const bodyStatusCode = apiResponse.data.statusCode;
      if (bodyStatusCode === 202) {
        const asyncResult = apiResponse.data.result as unknown as {
          requestId?: string;
        };
        if (!asyncResult?.requestId) {
          return {
            status: {
              type: 'success',
              data: { simulationStatus: 'unknown', message: '非同期レスポンスにrequestIdが含まれていません' },
            },
          };
        }

        // 202を受け取った時点で設定は保存済み。以降の失敗は再計算の状態として返す。
        try {
          const pollingResult = await pollAsyncProcess(asyncResult.requestId, { actionType: 'bulk-simulation' });
          if (pollingResult.status === 'failed') {
            return {
              status: {
                type: 'success',
                data: { simulationStatus: 'failed', message: pollingResult.errorMessage ?? 'シミュレーション処理が失敗しました' },
              },
            };
          }
          return { status: { type: 'success', data: { simulationStatus: 'completed' } } };
        } catch (e) {
          return {
            status: {
              type: 'success',
              data: {
                simulationStatus: 'unknown',
                message: e instanceof Error ? e.message : 'シミュレーション処理の完了を確認できませんでした',
              },
            },
          };
        }
      } else if (bodyStatusCode === 200) {
        const asyncFailedResult = apiResponse.data.result as unknown as {
          simulationEnqueued?: boolean;
          enqueueError?: string;
        };
        if (asyncFailedResult?.simulationEnqueued === false) {
          return {
            status: {
              type: 'success',
              data: {
                simulationStatus: 'failed',
                message: asyncFailedResult.enqueueError ?? 'シミュレーションリクエストが失敗しました',
              },
            },
          };
        }
      }
      return { status: { type: 'success', data: { simulationStatus: 'completed' } } };
    } catch (e) {
      return { status: { type: 'error', error: axiosErrorHandler(e) } };
    }
  }

  /**
   * 自由欄設定を保存する
   * @param request - リクエスト
   */
  async putBusinessFreeColumnSetting(request: ApiBusinessFreeColumnSetting): Promise<INetworkResult<void, IError>> {
    const axiosInstance = await customAxiosInstance('dbAccess');
    try {
      if (!axiosInstance) throw new Error('axiosInstance is undefined');
      const apiResponse = await axiosInstance.put('/setting/business-free-columns', request);
      switch (apiResponse.status) {
        case 200:
          return { status: { type: 'success', data: undefined } };
        default:
          throw new Error('想定外のレスポンス');
      }
    } catch (e) {
      return { status: { type: 'error', error: axiosErrorHandler(e) } };
    }
  }
}
